import { useCallback, useMemo, useState } from 'react';
import { type Member, type ProductHoldings, Stewardship } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, Dialog, EmptyState, Field, Select, Waiting } from '@beyond-js/ui/react';
import { Panel, Problem, useReauthenticate, useUnread } from '@accounts/ui';
import { lines } from './holdings';
import type { Products } from './products';

type Open = 'transfer' | 'delete' | undefined;

/**
 * An owner's two irreversible actions on an organization, apart from everything else (ACC-05, ACC-06):
 * **Transfer ownership** (another member becomes an owner, the owner an administrator) and **Delete
 * organization** (what each product holds is listed first; everyone loses access now and it is purged
 * after the grace, cancellable until then). Both are confirmed by typing the organization's name, and
 * both ask the person to prove it is them when their sign-in is not recent.
 */
export function Danger({ organization, name, members, products, onDone }: { organization: string; name: string; members?: Member[]; products: Products; onDone: (notice: string) => Promise<void> }) {
	const { text, client, identity, language, date, refresh } = usePresence();
	const stewardship = useMemo(() => new Stewardship(client), [client]);
	const reauthenticate = useReauthenticate();
	const [open, setOpen] = useState<Open>();
	const [typed, setTyped] = useState('');
	const [to, setTo] = useState('');
	const [held, setHeld] = useState<ProductHoldings[]>();
	// Why what the products hold could not be read, which never reads as nothing being held
	const [unheld, setUnheld] = useState<Refusal>();
	const [busy, setBusy] = useState(false);
	const [failure, setFailure] = useState<unknown>();
	const others = (members ?? []).filter(member => member.state === 'active' && member.account !== identity?.account.id);

	// Whether Accounts answered, which takes the read out of the page's outage while the dialog is open (D163)
	const holdings = useCallback(() => Refusal.answered(async () => setHeld(await stewardship.holdings(organization)), setUnheld), [stewardship, organization]);
	const outage = useUnread({ key: 'holdings', title: text('outage.holdings'), unread: unheld, run: holdings }, open === 'delete');

	function show(which: Open) {
		setOpen(which);
		setTyped('');
		setTo(others[0]?.account ?? '');
		setFailure(undefined);
		if (which === 'delete') {
			setHeld(undefined);
			void holdings();
		}
	}

	/** Sends a confirmed action once, and once more after proving it is them when the service asks */
	async function send(work: () => Promise<string>) {
		setBusy(true);
		setFailure(undefined);
		try {
			let notice: string;
			try {
				notice = await work();
			} catch (error) {
				if (!(Refusal.is(error) && error.code === 'REAUTHENTICATION_REQUIRED' && (await reauthenticate()))) throw error;
				notice = await work();
			}
			setOpen(undefined);
			await onDone(notice);
		} catch (error) {
			if (!(Refusal.is(error) && error.quiet)) setFailure(error);
			// Nothing came back, so it may have been done: the organizations are read again
			if (Refusal.is(error) && error.unknown) await refresh();
		} finally {
			setBusy(false);
		}
	}

	const transfer = () =>
		send(async () => {
			await stewardship.transfer(organization, to, typed);
			return text('transfer.done', { name: others.find(member => member.account === to)?.member ?? text('members.unnamed') });
		});
	const remove = () =>
		send(async () => {
			const closed = await stewardship.close(organization, typed);
			return text('delete.done', { organization: name, date: date(closed.purge) });
		});

	const purge = new Date(Date.now() + 30 * 86400000).toISOString();
	const matches = typed.trim() === name.trim();
	// The products' names join the page's notice while the deletion's dialog is open (D163)
	const unnamed = useUnread({ key: 'products', title: text('outage.products'), unread: products.unread, run: products.run }, open === 'delete') && !products.list;

	return (
		<Panel id="danger" title={text('danger.title')}>
			<div className="flex flex-wrap gap-2">
				{/* Drawn at once; handing over waits for the members it is offered to (LOAD-01) */}
				<Button variant="secondary" disabled={!members} onClick={() => show('transfer')}>
					{text('transfer.action')}
				</Button>
				<Button variant="danger" onClick={() => show('delete')}>
					{text('delete.action')}
				</Button>
			</div>
			<Dialog
				open={Boolean(open)}
				title={open === 'transfer' ? text('transfer.title', { organization: name }) : text('delete.title', { organization: name })}
				description={open === 'transfer' ? text('transfer.lead') : text('delete.lead', { date: date(purge) })}
				busy={busy}
				labels={Dialog.labels[language]}
				onClose={() => setOpen(undefined)}
				actions={
					<>
						<Button variant="quiet" onClick={() => setOpen(undefined)} disabled={busy}>
							{text('action.cancel')}
						</Button>
						<Button variant={open === 'delete' ? 'danger' : 'primary'} busy={busy} disabled={!matches || (open === 'transfer' && !to) || (open === 'delete' && held === undefined && !unheld)} onClick={() => void (open === 'transfer' ? transfer() : remove())}>
							{text(open === 'transfer' ? 'transfer.submit' : 'delete.submit')}
						</Button>
					</>
				}
			>
				<div className="flex flex-col gap-4">
					{failure ? <Problem error={failure} /> : null}
					{open === 'transfer' ? (
						others.length ? (
							<Field label={text('transfer.member')}>
								<Select value={to} onChange={event => setTo(event.target.value)} options={others.map(member => ({ value: member.account, label: `${member.member ?? text('members.unnamed')}${member.email ? ` (${member.email})` : ''}` }))} />
							</Field>
						) : (
							<Callout tone="warning" title={text('transfer.none')} />
						)
					) : null}
					{open === 'delete' ? (
						<div>
							<p className="text-body text-ink">{text('holdings.title')}</p>
							{held === undefined && !unheld ? <Waiting label={text('holdings.reading')} language={language} kind="lines" rows={2} frame={false} /> : null}
							{(outage && held === undefined) || (held && unnamed) ? <EmptyState kind="outage" frame={false} labels={EmptyState.labels[language]} /> : null}
							{unheld && !outage ? <Problem error={unheld} /> : null}
							{held && products.list && !lines(held, products.list, text).length ? <p className="text-small text-ink-muted">{text('holdings.none')}</p> : null}
							<ul className="mt-1 list-disc pl-5 text-small text-ink-muted">
								{(held && products.list ? lines(held, products.list, text) : []).map(line => (
									<li key={line}>{line}</li>
								))}
							</ul>
						</div>
					) : null}
					<Field label={text(open === 'transfer' ? 'transfer.confirm' : 'delete.confirm', { organization: name })}>
						<input value={typed} autoComplete="off" onChange={event => setTyped(event.target.value)} />
					</Field>
				</div>
			</Dialog>
		</Panel>
	);
}
